import { Vector3 } from '../../types/Vector3'
import { Quaternion } from '../../types/Quaternion'
import { LayoutInterface, AlignmentMode } from '../FieldTypes'
import { FieldParameters } from '../FieldParameters'
import { VectorUtils } from '../../../math/VectorUtils'
import { IndexTransforms } from '../../../math/IndexTransforms'
import { CurveUtils } from '../../../math/CurveUtils'

export class GridLayout implements LayoutInterface {
  // ==================== POSITION CALCULATION ====================
  
  /**
   * Calculate position for object at given index
   */
  public computePosition(index: number, totalCount: number, params: FieldParameters): Vector3 {
    // Convert index to grid coordinates
    const coords = IndexTransforms.indexToGrid(index, params.gridSize)
    
    // Calculate spacing
    const spacing = this.calculateSpacing(params, totalCount)
    
    // Get position on grid using field direction as plane normal
    const position = this.getPointOnFlexibleGrid(
      new Vector3(), // Center at origin
      spacing,
      coords.row,
      coords.col,
      params.gridSize,
      params.fieldDirection
    )
    
    return position
  }
  
  // ==================== ORIENTATION CALCULATION ====================
  
  /**
   * Calculate orientation for object at given index using 2D surface mathematics
   */
  public computeOrientation(index: number, totalCount: number, params: FieldParameters): Quaternion {
    const coords = IndexTransforms.indexToGrid(index, params.gridSize)
    const position = this.computePosition(index, totalCount, params)
    
    // Calculate grid coordinates as surface parameters (0-1)
    const uParameter = coords.col / (params.gridSize - 1 || 1)
    const vParameter = coords.row / (params.gridSize - 1 || 1)
    
    // Use the improved 2D surface orientation calculation with existing enums
    const { right, up, forward } = CurveUtils.calculate2DSurfaceOrientation(
      'plane', // Grid is always on a plane surface
      position,
      params.fieldDirection, // Plane normal
      null, // No sphere center for planes
      uParameter, // U coordinate (0-1)
      vParameter, // V coordinate (0-1)
      params.alignmentMode, // Already AlignmentMode enum
      params.upVectorMode // Already UpVectorMode enum
    )
    
    // Convert to quaternion using lookDir
    return VectorUtils.lookRotation(forward, up)
  }
  
  // ==================== TANGENT CALCULATION ====================
  
  /**
   * Calculate tangent vector (direction along grid)
   */
  public computeTangent(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const coords = IndexTransforms.indexToGrid(index, params.gridSize)
    return this.getGridDirection(coords, params)
  }
  
  // ==================== NORMAL CALCULATION ====================
  
  /**
   * Calculate normal vector (perpendicular to grid)
   */
  public computeNormal(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const position = this.computePosition(index, totalCount, params)
    const center = new Vector3()
    return VectorUtils.getInwardNormal(position, center)
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Get point on grid in any plane (based on field direction normal)
   */
  private getPointOnFlexibleGrid(
    center: Vector3,
    spacing: number,
    row: number,
    col: number,
    gridSize: number,
    planeNormal: Vector3
  ): Vector3 {
    // Calculate grid offset in local 2D coordinates
    const offsetU = (col - (gridSize - 1) / 2) * spacing
    const offsetV = (row - (gridSize - 1) / 2) * spacing
    
    // Create orthogonal basis vectors for the plane
    const normal = planeNormal.clone().normalize()
    
    // Create arbitrary tangent vector perpendicular to normal
    let tangent1: Vector3
    if (Math.abs(normal.x) < 0.9) {
      tangent1 = new Vector3().crossVectors(new Vector3(1, 0, 0), normal).normalize()
    } else {
      tangent1 = new Vector3().crossVectors(new Vector3(0, 1, 0), normal).normalize()
    }
    
    // Second tangent vector (completes orthogonal basis)
    const tangent2 = new Vector3().crossVectors(normal, tangent1).normalize()
    
    // Convert 2D grid coordinates to 3D position in the plane
    const position = center.clone()
      .add(tangent1.clone().multiplyScalar(offsetU))
      .add(tangent2.clone().multiplyScalar(offsetV))
    
    return position
  }
  
  /**
   * Calculate spacing between objects
   */
  private calculateSpacing(params: FieldParameters, totalCount: number): number {
    if (params.useObjectSpacing && params.objectBounds) {
      // Use object bounds for spacing
      const objectSize = params.objectBounds.size
      const maxSize = Math.max(objectSize.x, objectSize.y, objectSize.z)
      return maxSize * params.spacingMultiplier
    } else {
      // Use fixed cell size plus padding
      return params.cellSize + params.cellSpacing
    }
  }
  
  /**
   * Get main directional vector of the layout (grid plane normal)
   */
  public getDirectionalVector(params: FieldParameters): Vector3 {
    return params.fieldDirection.clone().normalize()
  }
  
  /**
   * Get grid direction for alignment (within the grid plane)
   */
  private getGridDirection(coords: { row: number; col: number }, params: FieldParameters): Vector3 {
    // Apply custom alignment vector if specified
    if (params.customAlignmentVector.length() > 0) {
      return params.customAlignmentVector.clone().normalize()
    }
    
    // Calculate direction within the grid plane
    const planeNormal = params.fieldDirection.clone().normalize()
    
    // Create a tangent vector within the plane (towards next column)
    let tangent: Vector3
    if (Math.abs(planeNormal.x) < 0.9) {
      tangent = new Vector3().crossVectors(new Vector3(1, 0, 0), planeNormal).normalize()
    } else {
      tangent = new Vector3().crossVectors(new Vector3(0, 1, 0), planeNormal).normalize()
    }
    
    return tangent
  }
  
  // ==================== ADVANCED FEATURES ====================
  
  /**
   * Get grid bounds
   */
  public getGridBounds(params: FieldParameters): { 
    min: Vector3
    max: Vector3
    center: Vector3
    size: Vector3
  } {
    const spacing = this.calculateSpacing(params, params.gridSize * params.gridSize)
    const halfSize = (params.gridSize - 1) * spacing / 2
    
    // Calculate bounds based on field direction (plane normal)
    const normal = params.fieldDirection.clone().normalize()
    
    // Create tangent vectors for the plane
    let tangent1: Vector3
    if (Math.abs(normal.x) < 0.9) {
      tangent1 = new Vector3().crossVectors(new Vector3(1, 0, 0), normal).normalize()
    } else {
      tangent1 = new Vector3().crossVectors(new Vector3(0, 1, 0), normal).normalize()
    }
    const tangent2 = new Vector3().crossVectors(normal, tangent1).normalize()
    
    // Calculate extreme points in the plane
    const corner1 = tangent1.clone().multiplyScalar(halfSize).add(tangent2.clone().multiplyScalar(halfSize))
    const corner2 = tangent1.clone().multiplyScalar(-halfSize).add(tangent2.clone().multiplyScalar(-halfSize))
    
    return {
      min: new Vector3(
        Math.min(corner1.x, corner2.x),
        Math.min(corner1.y, corner2.y),
        Math.min(corner1.z, corner2.z)
      ),
      max: new Vector3(
        Math.max(corner1.x, corner2.x),
        Math.max(corner1.y, corner2.y),
        Math.max(corner1.z, corner2.z)
      ),
      center: new Vector3(0, 0, 0),
      size: new Vector3(halfSize * 2, halfSize * 2, halfSize * 2)
    }
  }
  
  /**
   * Get neighbors of a grid position
   */
  public getNeighbors(
    index: number,
    params: FieldParameters,
    includeDiagonal: boolean = false
  ): number[] {
    const coords = IndexTransforms.indexToGrid(index, params.gridSize)
    const neighborCoords = IndexTransforms.getGridNeighbors(coords, params.gridSize, includeDiagonal)
    
    return neighborCoords.map(coord => 
      IndexTransforms.gridToIndex(coord.row, coord.col, params.gridSize)
    )
  }
  
  /**
   * Find closest grid point
   */
  public findClosestPoint(targetPoint: Vector3, totalCount: number, params: FieldParameters): {
    point: Vector3
    index: number
    distance: number
    coords: { row: number; col: number }
  } {
    let minDistance = Infinity
    let closestPoint = new Vector3()
    let closestIndex = 0
    let closestCoords = { row: 0, col: 0 }
    
    // Check all grid positions
    for (let i = 0; i < totalCount; i++) {
      const point = this.computePosition(i, totalCount, params)
      const distance = targetPoint.distanceTo(point)
      
      if (distance < minDistance) {
        minDistance = distance
        closestPoint = point
        closestIndex = i
        closestCoords = IndexTransforms.indexToGrid(i, params.gridSize)
      }
    }
    
    return {
      point: closestPoint,
      index: closestIndex,
      distance: minDistance,
      coords: closestCoords
    }
  }
  
  /**
   * Get distance between two grid positions
   */
  public getGridDistance(
    index1: number,
    index2: number,
    params: FieldParameters,
    metric: 'manhattan' | 'euclidean' = 'euclidean'
  ): number {
    const coords1 = IndexTransforms.indexToGrid(index1, params.gridSize)
    const coords2 = IndexTransforms.indexToGrid(index2, params.gridSize)
    
    if (metric === 'manhattan') {
      return IndexTransforms.gridManhattanDistance(coords1, coords2)
    } else {
      return IndexTransforms.gridEuclideanDistance(coords1, coords2)
    }
  }
  
  /**
   * Check if index is on grid border
   */
  public isOnBorder(index: number, params: FieldParameters): boolean {
    const coords = IndexTransforms.indexToGrid(index, params.gridSize)
    return coords.row === 0 || 
           coords.row === params.gridSize - 1 || 
           coords.col === 0 || 
           coords.col === params.gridSize - 1
  }
  
  /**
   * Check if index is in grid corner
   */
  public isInCorner(index: number, params: FieldParameters): boolean {
    const coords = IndexTransforms.indexToGrid(index, params.gridSize)
    return (coords.row === 0 || coords.row === params.gridSize - 1) &&
           (coords.col === 0 || coords.col === params.gridSize - 1)
  }
  
  /**
   * Get grid center index
   */
  public getCenterIndex(params: FieldParameters): number {
    const centerCoords = IndexTransforms.getGridCenter(params.gridSize)
    return IndexTransforms.gridToIndex(centerCoords.row, centerCoords.col, params.gridSize)
  }
  
  /**
   * Get spiral traversal order (for ripple effects)
   */
  public getSpiralTraversal(params: FieldParameters): number[] {
    const indices: number[] = []
    const visited = new Set<number>()
    const center = Math.floor(params.gridSize / 2)
    
    let x = center
    let y = center
    let dx = 0
    let dy = -1
    
    for (let i = 0; i < params.gridSize * params.gridSize; i++) {
      if (x >= 0 && x < params.gridSize && y >= 0 && y < params.gridSize) {
        const index = IndexTransforms.gridToIndex(y, x, params.gridSize)
        if (!visited.has(index)) {
          indices.push(index)
          visited.add(index)
        }
      }
      
      if (x === y || (x < 0 && x === -y) || (x > 0 && x === 1 - y)) {
        const temp = dx
        dx = -dy
        dy = temp
      }
      
      x += dx
      y += dy
    }
    
    return indices
  }
  
  /**
   * Get concentric rings (for ripple effects)
   */
  public getConcentricRings(params: FieldParameters): number[][] {
    const rings: number[][] = []
    const center = Math.floor(params.gridSize / 2)
    const maxRing = Math.floor(params.gridSize / 2)
    
    for (let ring = 0; ring <= maxRing; ring++) {
      const ringIndices: number[] = []
      
      for (let i = 0; i < params.gridSize * params.gridSize; i++) {
        const coords = IndexTransforms.indexToGrid(i, params.gridSize)
        const distance = Math.max(
          Math.abs(coords.row - center),
          Math.abs(coords.col - center)
        )
        
        if (distance === ring) {
          ringIndices.push(i)
        }
      }
      
      if (ringIndices.length > 0) {
        rings.push(ringIndices)
      }
    }
    
    return rings
  }
  
  /**
   * Get grid row
   */
  public getRowIndices(row: number, params: FieldParameters): number[] {
    const indices: number[] = []
    
    for (let col = 0; col < params.gridSize; col++) {
      const index = IndexTransforms.gridToIndex(row, col, params.gridSize)
      indices.push(index)
    }
    
    return indices
  }
  
  /**
   * Get grid column
   */
  public getColumnIndices(col: number, params: FieldParameters): number[] {
    const indices: number[] = []
    
    for (let row = 0; row < params.gridSize; row++) {
      const index = IndexTransforms.gridToIndex(row, col, params.gridSize)
      indices.push(index)
    }
    
    return indices
  }
} 